import React, { useState, useEffect, useRef, useCallback } from "react";
import theme from "../../data/theme";
import RosterBoard from "./RosterBoard";
import NominationBlock, { useSoldFlash, NOMINATION_RESERVE } from "./NominationBlock";
import BoardScaleControl, { useBoardScale, isTvMode, BoardScaleStepper, useIdleReveal } from "./BoardScale";
import ClipToggle, { useWatchClipsEnabled } from "./ClipToggle";
import NominationFlash from "./NominationFlash";
import DraftCountdown from "./DraftCountdown";
import DraftLog from "./DraftLog";
import PlayersLeft, { useHideTaken, HideTakenToggle } from "./PlayersLeft";
import { fetchPublishedBoard } from "../../utils/liveBoardPublish";
import { sanitizeLiveBoard, buildLiveTeams, liveRosterSlots, boardTotals, boardIsComplete } from "../../utils/liveBoard";
import { boardFocus } from "../../utils/boardFocus";
import SAMPLE_BOARD from "../../data/liveBoardSample.json";

/**
 * WatchPage — the read-only spectator view. One writer, many readers.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE BOARD vs THE ASSISTANT — read this before adding anything here.
 *
 *   THE BOARD is what everyone sees: the roster grid, who is nominated,
 *   whose turn it is. FACTS ABOUT THE AUCTION, SHOWN RAW — things that
 *   happened, or that follow from the rules. This file renders the board and
 *   only the board.
 *
 *   THE ASSISTANT is the publisher's: the entry row, My Cheatsheet, the
 *   room read, the tier cliff, STILL NEEDED, and every price ESTIMATE. It
 *   wraps the board on his screen and is never in this tree. He is bidding
 *   against the people reading this page; his read on the market is not
 *   theirs.
 *
 * THREE RULES DECIDE WHAT GOES HERE. Apply them in order; do not relitigate
 * them per feature.
 *
 *   1. Facts, never opinions. A sale price is a fact and appears on the
 *      roster cards. Our estimate of a player's value is an opinion and
 *      appears nowhere here.
 *
 *   2. 🔴 THE BOARD DOES NOT DO THE DERIVATION FOR YOU. "QB · 4 teams · 2
 *      need 2+" is a FACT, it passed rule 1, and it still came off this page
 *      (Aug 11, 2026, after Ben ran the board for real). The rosters are on
 *      screen, so a spectator can work out who needs a QB by looking — that
 *      is fine. Rolling it up into one line hands every opponent the read in
 *      a glance, and the publisher is bidding against those people. Rule 1
 *      alone does not decide that case, which is exactly why rule 2 is
 *      written down instead of inferred.
 *
 *   3. 🔴 WHERE RULE 2's LINE FALLS — because rule 2 alone would strip the
 *      board bare. THE BOARD DOES ARITHMETIC A PERSON AT THE TABLE COULD DO
 *      IN THEIR HEAD; IT DOES NOT DO SYNTHESIS THEY COULD NOT.
 *
 *      `Max $n` on the roster cards STAYS on /watch (Ben's call, Aug 11,
 *      2026, and watchPage.test.js pins it so a later reading of rule 2
 *      cannot quietly take it). It is one subtraction from two numbers
 *      already printed on that same card. "4 teams need a QB" is an
 *      aggregation across twelve rosters that nobody at a real auction can
 *      perform.
 *
 *      The same cut, said the other way: max bid is a CONSTRAINT — what a
 *      team CAN bid, a rule of the auction. Still-needed is INTENT — what
 *      they WANT. Constraints are public; intent is the read you are not
 *      handing over. Both cuts give the same answer on every case so far, so
 *      if they ever disagree, that is the case to bring to Ben rather than
 *      settle in a commit.
 *
 * This is structural, not a convention to remember: an assistant component
 * cannot render in the shared tree if it is never imported into it, and
 * watchPage.test.js asserts the import list. If you find yourself wanting
 * to pass `dynamicPrices` into anything below, that is the signal you are
 * about to move an assistant component into the board.
 * ══════════════════════════════════════════════════════════════════════
 *
 * 🔴 A SPECTATOR CANNOT MUTATE ANYTHING. No entry row, no Start over, no edit,
 * no undo, no share controls. This component is handed a share id and renders
 * whatever the publisher last posted.
 *
 * 🔴 IT MUST SAY WHEN IT LAST UPDATED. Eleven people make decisions off this.
 * A board frozen at pick 40 that still looks live is worse than one that admits
 * it is stale, so the age of the data is always on screen and changes
 * appearance past STALE_AFTER.
 *
 * "No board yet" is NOT an error — it is waiting for the first pick, and it
 * says so.
 *
 * Renders OUTSIDE PlayerCardProvider (same as /scout), which is why RosterBoard
 * takes an optional click handler instead of calling usePlayerCard: that hook
 * destructures a null context and throws when there is no provider.
 */
const POLL_MS = 4000;
const STALE_AFTER_MS = 60_000;

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE SAMPLE BOARD — a reserved share id, not a second surface.
 *
 * Nobody types eleven team names to find out what the thing does. /watch/sample
 * is the answer: a real-shaped board, mid-auction, on the exact component every
 * spectator gets, so the landing page can show the product instead of
 * describing it.
 *
 * 🔴 IT MAKES NO NETWORK CALL AND IT NEVER EXPIRES. A real share link is
 * published to api/live-board and dies 14 days after the draft; a demo that
 * 404s is worse than no demo. The fixture is bundled, so the page cannot be
 * empty, stale, or down.
 *
 * 🔴 NOBODY REAL IS ON IT. The team names are invented and the prices are the
 * current board's own, jittered. It is not a copy of anyone's league.
 *
 * ⚠️ IT IS STILL A WATCH PAGE. No entry controls, no edit, no undo — the same
 * component under the same rules as the header above. If a control ever appears
 * here that a spectator does not get, that is the bug.
 * ══════════════════════════════════════════════════════════════════════
 */
export const SAMPLE_SHARE_ID = "sample";

function ago(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
}

export default function WatchPage({ shareId }) {
  const isSample = shareId === SAMPLE_SHARE_ID;
  // Seeded in the initialiser rather than in an effect: the sample has nothing
  // to wait for, and a "Loading the board…" flash on a page whose content is in
  // the bundle would be a loading state invented for its own sake.
  const [state, setState] = useState(() => {
    if (shareId !== SAMPLE_SHARE_ID) return { status: "loading" };
    const board = sanitizeLiveBoard(SAMPLE_BOARD);
    return board ? { status: "ok", board, updatedAt: null } : { status: "waiting" };
  });
  const [lastOk, setLastOk] = useState(null);     // ms epoch of our last good fetch
  const [now, setNow] = useState(() => Date.now());
  const abortRef = useRef(null);
  // 🔴 A VIEWER PREFERENCE, NOT BOARD CONTENT. Each spectator reads this page on
  // their own device — a phone in a hand, a TV across the room — so the size is
  // theirs and lives in their localStorage. It is never published, and it is not
  // an assistant component: it says nothing about the auction.
  const [scale, setScale] = useBoardScale();
  /**
   * 🔴 CLIPS HERE ARE OPT-IN AND DEFAULT OFF — see the header on
   * WATCH_CLIPS_KEY. This page used to render no video at all, which was the
   * blunt form of a rule about BANDWIDTH, not about what a spectator is allowed
   * to see: eleven phones on cellular should pull nothing, a TV on wifi that
   * asked should get everything. It is a viewer preference like the scale, in
   * this device's localStorage, never published and never in board state.
   *
   * ⚠️ NOT AN ASSISTANT COMPONENT. A highlight clip is decoration attached to a
   * player the whole room can already see named on this page. It carries no
   * estimate, no rank and no read on the market, so the three rules in the
   * header do not bite — rule 1 is about opinions, and this is a video.
   */
  const [clipsOn, setClipsOn] = useWatchClipsEnabled();
  /**
   * Which of the two shared views is up. "What did I miss" is the single most
   * common spectator question and the draft log is its answer, so it is here —
   * it passes all three rules above: it is facts, it does no derivation, and it
   * synthesises nothing a person at the table could not have written down
   * themselves. READ-ONLY, no onEditSale, same as the roster grid.
   */
  const [view, setView] = useState("rosters");
  /**
   * Whether the pool hides the names already gone. A viewer preference like the
   * scale and the clips switch — this device's localStorage, never published.
   * See the header on HIDE_TAKEN_KEY for why it defaults off.
   */
  const [hideTaken, setHideTaken] = useHideTaken();
  /**
   * 🔴 THE STEPPER IS OFFERED ONLY WHILE SOMEBODY IS THERE. This page is cast
   * to a TV across a room and left alone for two hours; a control parked in
   * the corner of it is chrome that never earns its place. The same page in a
   * hand gets a move, a scroll or a tap within seconds of anybody wanting it.
   * Starts hidden, because the first render of a cast page is the one nobody
   * has touched. The scale itself is this device's own and is never published
   * — see the header on BOARD_SCALE_KEY.
   */
  const scaleShown = useIdleReveal();

  const poll = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const res = await fetchPublishedBoard(shareId, ac.signal);
    if (ac.signal.aborted) return;
    if (res.status === "ok") {
      const board = sanitizeLiveBoard(res.board);
      if (board) {
        setState({ status: "ok", board, updatedAt: res.updatedAt });
        setLastOk(Date.now());
        return;
      }
      setState({ status: "waiting" });
      return;
    }
    // A failed poll does NOT wipe a board already on screen — it goes stale and
    // says so, which is far more useful to a room than an empty page.
    setState(prev => (prev.status === "ok" ? prev : { status: res.status }));
  }, [shareId]);

  // No id, nothing to poll for. Guarded here rather than by an early return:
  // the hooks have to run unconditionally, and there is no board to ask about.
  useEffect(() => {
    if (!shareId || isSample) return undefined;   // the sample is already here
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { clearInterval(t); abortRef.current?.abort(); };
  }, [poll, shareId, isSample]);

  // Separate ticker so the "updated Ns ago" line counts up between polls rather
  // than jumping every four seconds.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /**
   * 🔴 THE BATON, DECIDED ONCE — and this is the surface it matters most on.
   * The banner and the grid are the two halves of one message, and a spectator
   * across a room is reading the glow rather than the text. Called before the
   * early returns below, unconditionally, because it is a hook: a poll that
   * fails must not change which hooks run.
   */
  const sold = useSoldFlash(state.status === "ok" ? state.board : null);
  const focus = boardFocus(state.status === "ok" ? state.board : null, sold);

  // 🔴 TV MODE, and this is the surface that most needs it: a second device
  // opened at /live gets its own empty board, so if anyone but the operator
  // drives the TV — or it is opened in a TV browser directly — this page is the
  // only one that shows the auction at all.
  const tv = isTvMode(scale);
  const page = {
    maxWidth: tv ? "none" : 1500, margin: "0 auto",
    padding: tv ? "8px 10px 24px" : "16px 20px 60px",
    minHeight: "100vh",
  };
  const shell = (children) => (
    <div style={{ background: theme.colors.bg.app, minHeight: "100vh" }}>
      <div data-testid="watch-page" style={page}>{children}</div>
    </div>
  );

  // 🔴 NO BOARD ID. Before "loading", because with no id there is nothing to
  // load and the loading state would sit there forever.
  //
  // App routes the whole /watch PATH here, not just /watch?b=… — THE SIDE
  // EFFECTS ARE WHY, not the copy. Falling through to the builder handed a
  // spectator with a truncated link the entire builder on their phone: the
  // whole hook chain, a localStorage migration and the roster fetch, on a
  // device with no league saved for any of it to read. So "the link is
  // incomplete" is a state of THIS surface.
  //
  // Nothing is broken and nothing is stale — the link is short. It says that,
  // and it does not offer a retry for something retrying cannot fix.
  if (!shareId) {
    return shell(
      <div style={{ maxWidth: 520, margin: "80px auto", textAlign: "center" }}>
        <div style={{
          fontFamily: theme.fonts.display, fontSize: theme.fontSize.title,
          color: theme.colors.text.primary, fontWeight: theme.fontWeight.heavy, marginBottom: 10,
        }}>This link is missing its board id</div>
        <div style={{ color: theme.colors.text.muted, fontSize: theme.fontSize.body, lineHeight: 1.6 }}>
          A watch link ends in <code>?b=</code> and a short code. This one arrived without it,
          most often a link that got cut off in a text. Ask whoever shared it to send the whole thing.
        </div>
      </div>
    );
  }

  if (state.status === "loading") {
    return shell(<div style={{ color: theme.colors.text.muted, padding: 40, textAlign: "center" }}>Loading the board…</div>);
  }

  if (state.status !== "ok") {
    const waiting = state.status === "waiting";
    return shell(
      <div style={{ maxWidth: 520, margin: "80px auto", textAlign: "center" }}>
        <div style={{
          fontFamily: theme.fonts.display, fontSize: theme.fontSize.title,
          color: theme.colors.text.primary, fontWeight: theme.fontWeight.heavy, marginBottom: 10,
        }}>{waiting ? "Waiting for the first pick" : "Can't reach the board"}</div>
        <div style={{ color: theme.colors.text.muted, fontSize: theme.fontSize.body, lineHeight: 1.6 }}>
          {waiting
            ? "This link is live. Nothing has been entered yet. The board will appear here as picks come in."
            : "The connection dropped. This page keeps retrying every few seconds."}
        </div>
      </div>
    );
  }

  const { board, updatedAt } = state;
  const age = now - (lastOk || now);
  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 A DRAFT THAT FINISHED IS NOT A FEED THAT BROKE.
   *
   * Sixty seconds after the final pick, STALE_AFTER_MS used to fire and this
   * page turned yellow: "not updating — last change 1m ago", plus a banner
   * telling eleven people the person entering picks may have lost connection.
   * Nothing was wrong. The board stopped changing because the auction ended —
   * so a successful finish rendered as a broken feed, on the one screen the
   * whole room is looking at, at the one moment they are all looking at it.
   *
   * The staleness alarm means "this board SHOULD be changing and isn't". On a
   * complete board that premise is false, so the alarm is suppressed rather
   * than reworded.
   *
   * ⚠️ GATED ON THE BOARD BEING COMPLETE — not on the poll going quiet, not on
   * a timer, not on updatedAt age. A time-based guess reintroduces the same bug
   * from the other direction: a real dropout in the last two rounds would be
   * silently reclassified as "the draft must have ended". `boardIsComplete` is
   * the same pure function the roster cards' DONE badge reads, so the page
   * cannot say FINAL while a card still shows an empty slot.
   * ══════════════════════════════════════════════════════════════════════
   */
  const complete = boardIsComplete(board);
  // The sample never goes stale: there is no publisher and nothing to lose
  // connection to, so the alarm's premise ("this board SHOULD be changing") is
  // false for the same reason it is false on a finished draft.
  const stale = !complete && !isSample && age > STALE_AFTER_MS;
  const teams = buildLiveTeams(board);
  const slots = liveRosterSlots(board);
  const totals = boardTotals(board);

  return shell(
    <>
      {/* ══════════════════════════════════════════════════════════════
          🔴 THE WAY OUT OF THE SAMPLE, AND ONLY OUT OF THE SAMPLE.
          Somebody arrives here from the landing page or the setup screen with
          no board of their own — the whole point is that they have not built
          one yet — and this page has no chrome, no logo and no link anywhere.
          Convinced by the demo, they had nowhere to go but the back button.

          ⚠️ NEVER ON A REAL WATCH LINK. That page is read by eleven people on
          their phones and cast to a TV across a room; a nav bar on it is
          somewhere to accidentally tap mid-auction and a row of board on the
          one screen nobody can scroll. Gated on `isSample`, which is a
          reserved id, not a heuristic.
          ══════════════════════════════════════════════════════════════ */}
      {isSample && (
        <div data-testid="sample-bar" style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          marginBottom: 10, padding: "7px 12px", borderRadius: theme.radius.md,
          background: theme.helpers.brandBg(0.08),
          border: `1px solid ${theme.colors.border.brandLight}`,
          fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
        }}>
          <span>This is a sample board.</span>
          {/* 🔴 ?setup=1, NOT A BARE /live. Whoever clicks this has decided to
              run a draft; a bare /live would drop them inside a half-finished
              board from three weeks ago. See SETUP_PARAM in LiveBoard.jsx. */}
          <a href="/live?setup=1" style={{ color: theme.colors.brand.accent, textDecoration: "none", fontWeight: theme.fontWeight.bold }}>
            Set up your own
          </a>
          <a href="/" style={{ color: theme.colors.text.faint, textDecoration: "none" }}>
            Back to Mock1
          </a>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{
          fontFamily: theme.fonts.display, fontSize: theme.fontSize.heading,
          color: theme.colors.text.primary, fontWeight: theme.fontWeight.heavy,
        }}>{board.leagueName || "Live Board"}</span>
        {/* Facts only, and folded into the line that already carried most of
            them rather than repeated underneath it. Total spent is the sum of
            what the room actually paid — it is on the roster cards already. */}
        <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, fontVariantNumeric: "tabular-nums" }}>
          {/* "0 slots left" is technically true and reads as an oversight. The
              same facts, said as an ending. */}
          {totals.filled} sold · {complete ? "draft complete" : `${totals.openSlots} slots left`}
          {" · "}${totals.spent} spent
          {" · "}${board.budget} · {board.teamNames.length} teams
        </span>

        {/* 🔴 THE STALENESS MARKER. Always present, visibly different past a
            minute. A board that looks live but is not is the failure mode this
            whole view has to avoid. */}
        {/* 🔴 THE STALENESS MARKER IS REPLACED, NOT REWORDED, ON A COMPLETE
            BOARD. "updated 43m ago" is as wrong as "not updating" once the
            auction is over — both answer a question nobody is asking any more.
            FINAL answers the one they are. */}
        {/* 🔴 THE SAMPLE SAYS SO, IN THE SLOT THE FRESHNESS MARKER USES. "updated
            0s ago" on a fixture is a freshness claim about a board that has no
            freshness, and it is the one line on this page a room reads to
            decide whether to trust what is under it. Replaced, not reworded —
            the same call the FINAL state makes. */}
        {isSample ? (
          <span
            data-testid="watch-sample"
            style={{
              marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
              padding: "3px 10px", borderRadius: 999, letterSpacing: "0.12em",
              color: theme.colors.brand.accent,
              background: theme.helpers.brandBg(0.12),
              border: `1px solid ${theme.colors.border.brandLight}`,
            }}
          >SAMPLE BOARD</span>
        ) : (
        <span
          data-testid={complete ? "watch-final" : "watch-freshness"}
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
            padding: "3px 10px", borderRadius: 999,
            color: stale ? "#fde68a" : "#bbf7d0",
            background: stale ? "rgba(234,179,8,0.14)" : "rgba(34,197,94,0.12)",
            border: `1px solid ${stale ? "rgba(234,179,8,0.45)" : "rgba(34,197,94,0.35)"}`,
            letterSpacing: complete ? "0.12em" : undefined,
          }}
        >
          <span style={{
            width: 7, height: 7, borderRadius: "50%",
            background: stale ? "#eab308" : "#22c55e",
          }} />
          {complete ? "FINAL"
            : stale ? `not updating, last change ${ago(age)}`
              : `updated ${ago(age)}`}
        </span>
        )}
        <span style={{ fontSize: theme.fontSize.micro, color: theme.colors.text.faint }}>read-only</span>
        {/* Keeps its box whether or not it is shown, so appearing cannot
            reflow the board under a room reading it mid-auction. */}
        <BoardScaleStepper
          scale={scale} onChange={setScale}
          visible={scaleShown} testId="watch-scale-stepper"
        />
      </div>

      {/* Text size for THIS device. Casting mirrors the tab at the same CSS
          width, so no media query can tell a 65" TV from the laptop driving
          it — see BoardScale.jsx. */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <ClipToggle on={clipsOn} onChange={setClipsOn} />
        <BoardScaleControl scale={scale} onChange={setScale} />
      </div>

      {stale && (
        <div style={{
          background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.35)",
          borderRadius: theme.radius.md, padding: "8px 14px", marginBottom: 10,
          color: "#fde68a", fontSize: theme.fontSize.caption,
        }}>
          This board has not changed in over a minute. It may be between nominations, or the
          person entering picks may have lost connection. Treat what is below as of{" "}
          {updatedAt ? new Date(updatedAt).toLocaleTimeString() : "the time shown"}.
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          🔴 THE NOMINATION-HEIGHT RESERVE, and this surface needs it as much as
          the operator's: at TV the grid must not jump ~130px every time a
          player goes up and back on every sale. The countdown is INSIDE it —
          it vanishes for good at the first sale, so left outside it would move
          the grid once through a third door.

          ⚠️ The stale banner above is deliberately NOT in here. It is an alarm,
          it only exists when the feed has already stopped, and squashing it
          into a fixed block to protect a layout that is by then reporting
          nothing new is the wrong way round.
          ══════════════════════════════════════════════════════════════ */}
      <div style={{
        marginBottom: 12, position: "relative",
        minHeight: tv ? NOMINATION_RESERVE : undefined,
        display: "flex", flexDirection: "column",
      }}>
        {/* 🔴 OUT OF FLOW AT TV. minHeight is a floor, so a sibling inside this
            column ADDS to the block's height rather than being absorbed by it —
            countdown + waiting together measure past the reserve, and the grid
            would drop back at pick one. Rendered whole, over the panel, never
            clipped. In flow below TV, exactly as it always was. */}
        <div
          data-testid="board-notices"
          style={tv
            ? { position: "absolute", top: 0, left: 0, right: 0, zIndex: 5 }
            : undefined}
        >
          {/* 🔴 THE PRE-DRAFT STATE. "Waiting for the first pick" is exactly
              right at 7:59pm and says nothing useful a week out, which is when
              the link actually gets opened and forwarded. This is a FACT about
              the auction — when it starts — so it belongs on the shared board
              by rule 1, and it is rendered from the published instant in each
              viewer's own zone. It disappears for good at the first sale. */}
          <DraftCountdown board={board} />
        </div>

        {/* WHO IS NOMINATED RIGHT NOW — the single most valuable thing a
            spectator can see, and the reason the feed matters DURING the
            bidding rather than only after it.
            NOT the cheat sheet: that view is the publisher's own marks.
            Both states, because both are facts: who is up, or who the room is
            waiting on and how long they have had. The clock is derived from the
            published board's own timestamps, so it reads the same here as on
            the operator's screen. */}
        <NominationBlock board={board} focus={focus} readOnly showClips={clipsOn} fill={tv} />
      </div>

      {/* The same switch governs the beat, exactly as the operator's one toggle
          does. It costs no bandwidth the card has not already paid — same clip,
          same URL, same request — and on a room-facing TV it is most of the
          reason to have asked for clips at all. */}
      <NominationFlash board={board} enabled={clipsOn} />

      {/* The STILL NEEDED chip row used to sit here. See rule 2 in the header:
          it is a fact, and it came off anyway. It lives on the operator's
          screen (LiveBoard.jsx) — reclassified, not deleted. */}

      {/* The two shared views. Same tab shape as the operator's board, minus
          MY CHEATSHEET — those are his marks, not the room's.

          ⚠️ CHUNK 5 SCOPING: this row is NEW above the grid on /watch, which
          previously had nothing here. Its height counts against the TV budget
          on this surface and the chunk-5 measurement was scoped when it did not
          exist — measure it rather than assuming the space above the grid here
          is the two rows the brief lists. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {[["rosters", "ROSTERS"], ["log", "DRAFT LOG"], ["pool", "PLAYERS LEFT"]].map(([key, text]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              background: view === key ? theme.colors.brand.orange : "transparent",
              color: view === key ? "#fff" : theme.colors.text.muted,
              border: `1px solid ${view === key ? theme.colors.brand.orange : theme.colors.border.default}`,
              borderRadius: theme.radius.md, padding: "5px 14px", cursor: "pointer",
              fontFamily: "inherit", fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.bold, letterSpacing: "0.04em",
            }}
          >{text}</button>
        ))}
        {/* Only where it does something. A control for a view you are not
            looking at is a control nobody can connect to an effect. */}
        {view === "pool" && <HideTakenToggle on={hideTaken} onChange={setHideTaken} />}
      </div>

      {view === "log" ? (
        /* No onEditSale. A spectator reads, never corrects — the same reason
           the grid gets no onAdvanceNominator. */
        <DraftLog board={board} />
      ) : view === "pool" ? (
        /* 🔴 REPLACES THE GRID, never stacks on it — the TV budget is ~858px on
           a 1080p cast and a third view that cost vertical space would undo the
           fit work the scale control exists for. Facts only: public Underdog
           ADP, name and NFL team, no rank number and no price. See the header
           on PlayersLeft.jsx for why it may never touch posRank. */
        <PlayersLeft board={board} scale={scale} hideTaken={hideTaken} />
      ) : (
        /* Whose turn it is, and who just won a player, are facts, so the baton
           belongs here — the TV is the entire point of it.
           No onAdvanceNominator: a spectator reads, never corrects. */
        <RosterBoard
          teams={teams} rosterSlots={slots} scale={scale} fullNames={tv}
          focus={focus}
          // A team with no roster spots left cannot bid on anything — the same
          // class of fact as `Max $n`, which rule 3 in the header above keeps
          // on this surface deliberately.
          board={board}
        />
      )}
    </>
  );
}
